import "./MyPath.scss";

import { Container } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import dayjs from "dayjs";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useParams } from "react-router-dom";

import { PageTitle } from "@thrivetribe/gloji.ui.molecules.page-title";
import { SILVER_EXTRA_LIGHT } from "@thrivetribe/gloji.enums.brand-colors";
import { greyVelvet } from "../../../assets/img/bg";
import { CircularLoader } from "../../atoms/Loaders";
import { SectionHeader, SectionHeading, SectionIntro } from "../../atoms/SectionHeader";
import useUserId from "../../hooks/useUserId";
import { useProgrammeOverviews } from "../../hooks/useProgrammeOverviews";
import useCompletedSteps from "../../hooks/useCompletedSteps";
import CurrentStepCard from "../../molecules/CurrentStepCard";
import HeroBanner from "../../organisms/HeroBanner";
import PageBackground from "../../organisms/PageBackground";
import ProgrammeOverview from "../../organisms/ProgrammeOverview";
import ProgrammeStates from "../../../enums/programmeStates";
import Stepper from "../../atoms/Stepper";
import { useGetProfileByIdQuery } from "../../../redux/profileApiV3";
import { Programmes } from "../../../data/Programme";

const MyPath = () => {
  const [overview, setOverview] = useState();
  const [nextLockedStep, setNextLockedStep] = useState(null);
  const [userId] = useUserId();
  let { programmeSlug } = useParams();
  const completedSteps = useCompletedSteps(overview?.steps);

  const profileByIdResponse = useGetProfileByIdQuery(userId ?? skipToken);

  const {
    data: programmeOverviews,
    isSuccess: overviewsSuccess,
    isUninitialized: overviewsUninit,
  } = useProgrammeOverviews(profileByIdResponse);
  useEffect(() => {
    if (!overviewsSuccess || overviewsUninit) {
      return;
    }

    const currentProgramme = Programmes.find((x) => x.slug === programmeSlug);
    const currentProgrammeOverview = programmeOverviews.find(
      (x) => x.programme.id === currentProgramme.programmeId
    );
    setOverview(currentProgrammeOverview);
  }, [overviewsSuccess, overviewsUninit, programmeOverviews, programmeSlug]);

  useEffect(() => {
    if (overview?.steps === undefined) return;
    const next = overview.steps.find((step) => step.locked);
    if (next === undefined) return;
    setNextLockedStep(next);
  }, [overview]);

  const title = overview?.programme?.title !== undefined ? "| " + overview.programme.title : "";

  return (
    <PageBackground bgImage={greyVelvet}>
      <Helmet>
        <title>My path {title} | Gloji</title>
      </Helmet>
      {overviewsSuccess && !overviewsUninit ? (
        <div className="my-path-page">
          {overview != null && overview?.programme?.numberOfSteps != null && (
            <HeroBanner>
              <PageTitle color={SILVER_EXTRA_LIGHT}>{overview.programme.title}</PageTitle>
              <Stepper
                totalSteps={overview?.programme?.numberOfSteps - 1}
                completedSteps={completedSteps}
                currentStep={
                  overview?.nextAction != null
                    ? overview.nextAction.stepNumber
                    : nextLockedStep !== null
                    ? nextLockedStep.stepNumber - 1
                    : overview.programme.numberOfSteps - 1
                }
              />
              <CurrentStepCard />
            </HeroBanner>
          )}

          <Container className="main-content__container">
            <section className="">
              <SectionHeader className="programme-header">
                <SectionHeading size="h2">Your journey so far</SectionHeading>
                <SectionIntro className="programme-intro">
                  Welcome to your path. Take a look back at the steps you've completed so far. Watch
                  again, or see what's just round the corner.
                </SectionIntro>
                {overview?.state === ProgrammeStates.WAITING_UNLOCK && nextLockedStep != null && (
                  <SectionIntro className="programme-intro programme-intro--unlock-date">
                    {nextLockedStep.title} unlocks{" "}
                    {dayjs(nextLockedStep?.unlockDate).format("DD MMMM")}
                  </SectionIntro> 
                )}
              </SectionHeader>
              {overview?.state !== undefined &&
                overview?.startDate !== undefined &&
                overview?.steps !== undefined && <ProgrammeOverview programmeOverview={overview} />}
            </section>
          </Container>
        </div>
      ) : (
        <CircularLoader color="primary" size={40} />
      )}
    </PageBackground>
  );
};

export default MyPath;
